import { type ReactNode } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'

import Icon from '@components/utils/Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'
import { SmsDirection, SmsMessageKind } from '@frontend/graphql/types.generated'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('components.sms_conversation')

export type SmsMessageView = {
  id: string
  kind: SmsMessageKind
  direction: SmsDirection
  body: string
  sentAt: string
  senderName?: string | null
  groupName?: string | null
  eventName?: string | null
  blastPath?: string | null
}

export type SmsConversationPage = {
  totalCount: number
  totalPages?: number | null
  nodes: SmsMessageView[]
}

export type SmsConversationProps = {
  messages: SmsMessageView[]
  footer?: ReactNode
  ariaLabel?: string
}

type DayGroup = { key: string; label: string; messages: SmsMessageView[] }

function groupByDay(messages: SmsMessageView[]): DayGroup[] {
  const groups: DayGroup[] = []

  for (const message of messages) {
    const day = dayjs(message.sentAt)
    const key = day.format('YYYY-MM-DD')
    const current = groups.at(-1)

    if (current?.key === key) {
      current.messages.push(message)
    } else {
      groups.push({ key, label: day.format('D MMMM YYYY'), messages: [message] })
    }
  }

  return groups
}

type MessageMetaText = { visible: string; aria: string }

function outboundVisible(message: SmsMessageView, time: string): string {
  return [message.senderName, message.groupName, time].filter(Boolean).join(' · ')
}

function messageMeta(message: SmsMessageView, time: string): MessageMetaText {
  const sender = message.senderName ?? t('unknown_sender')
  const group = message.groupName ?? t('unknown_group')

  if (message.kind === SmsMessageKind.Blast) {
    return { visible: outboundVisible(message, time), aria: t('sent_bulk_aria', { sender, group, time }) }
  }

  if (message.direction === SmsDirection.Outbound) {
    return { visible: outboundVisible(message, time), aria: t('sent_aria', { sender, group, time }) }
  }

  return { visible: time, aria: t('received_aria', { time }) }
}

export function SmsConversation({ messages, footer, ariaLabel }: SmsConversationProps) {
  const dayGroups = groupByDay(messages)

  return (
    <TooltipProvider>
      <div role="log" aria-label={ariaLabel ?? t('conversation_label')} className="tw:flex tw:flex-col tw:gap-2">
        {dayGroups.map((group) => (
          <div key={group.key} className="tw:flex tw:flex-col tw:gap-2">
            <SmsDayDivider label={group.label} />
            {group.messages.map((message) =>
              message.kind === SmsMessageKind.Blast ? (
                <SmsBlastBubble key={message.id} message={message} />
              ) : (
                <SmsMessageBubble key={message.id} message={message} />
              )
            )}
          </div>
        ))}
        {footer}
      </div>
    </TooltipProvider>
  )
}

function SmsDayDivider({ label }: { label: string }) {
  return (
    <div className="tw:pointer-events-none tw:sticky tw:top-0 tw:z-10 tw:flex tw:justify-center tw:py-1">
      <span role="separator" aria-label={label} className="tw:rounded-full tw:bg-gray-100 tw:px-3 tw:py-0.5 tw:text-xs tw:font-medium tw:text-gray-true-600">
        {label}
      </span>
    </div>
  )
}

function MessageMeta({ text }: { text: string }) {
  return (
    <span aria-hidden className="tw:mt-0.5 tw:flex tw:items-center tw:gap-1 tw:px-1 tw:text-[11px] tw:text-gray-true-500">
      {text}
    </span>
  )
}

function SmsBlastBubble({ message }: { message: SmsMessageView }) {
  const time = dayjs(message.sentAt).format('h:mm A')
  const group = message.groupName ?? t('unknown_group')
  const meta = messageMeta(message, time)

  const tooltip = message.eventName
    ? t('bulk_tooltip_event', { event: message.eventName })
    : t('bulk_tooltip_group', { group })

  const headerClass = 'tw:mb-1 tw:inline-flex tw:items-center tw:gap-1 tw:py-1 tw:text-xs tw:font-medium tw:text-sky-700'
  const headerContent = (
    <>
      <Icon type="announcement-02" width={14} height={14} />
      {t('bulk_header')}
    </>
  )

  return (
    <article aria-label={meta.aria} className="tw:flex tw:flex-col tw:items-end">
      <div className="tw:max-w-[75%] tw:rounded-2xl tw:rounded-br-sm tw:bg-sky-50 tw:px-3 tw:py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            {message.blastPath ? (
              <a href={message.blastPath} target="_blank" rel="noopener noreferrer" className={clsx(headerClass, 'tw:hover:underline tw:focus-visible:underline')}>
                {headerContent}
                <Icon type="external-link" width={12} height={12} />
              </a>
            ) : (
              <span tabIndex={0} className={headerClass}>{headerContent}</span>
            )}
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
        <div className="tw:whitespace-pre-wrap tw:wrap-break-word tw:text-sm tw:text-gray-900">{message.body}</div>
      </div>
      <MessageMeta text={meta.visible} />
    </article>
  )
}

function SmsMessageBubble({ message }: { message: SmsMessageView }) {
  const isOutbound = message.direction === SmsDirection.Outbound
  const time = dayjs(message.sentAt).format('h:mm A')

  const meta = messageMeta(message, time)

  return (
    <article aria-label={meta.aria} className={clsx('tw:flex tw:flex-col', isOutbound ? 'tw:items-end' : 'tw:items-start')}>
      <div
        className={clsx(
          'tw:max-w-[75%] tw:whitespace-pre-wrap tw:wrap-break-word tw:rounded-2xl tw:px-3 tw:py-2 tw:text-sm tw:text-gray-900',
          isOutbound ? 'tw:rounded-br-sm tw:bg-primary-50' : 'tw:rounded-bl-sm tw:bg-gray-100'
        )}
      >
        {message.body}
      </div>
      <MessageMeta text={meta.visible} />
    </article>
  )
}
